import React, {
  useMemo,
  useCallback,
  useState,
  useEffect
} from "react";
import { QuickPickProps } from "../types";
import { MarketButton } from "../../MarketButton/MarketButton";
import cx from "components/src/utils/cx";
import get from "lodash-es/get";
import { QuickPickAvatar } from "./QuickPickAvatar";
import { QuickPickLegs } from "./QuickPickLegs";
import { computeLegs } from "../helpers/computeLegs";
import parseIconString, { IconStringReturnType } from "components/src/utils/parseIconString";
import { Card } from "components/src/components/Card/Card";
import { Button } from "components/src/components/Button/Button";
import { SizeType } from "shared/src/types";

import { CardOnExpandType } from "components/src/components/Card/types";
import { getTeamInfo } from "shared/src/hooks/getTeamInfo";
import { getCompetitionInfo } from "shared/src/hooks/getCompetitionInfo";
import { Background } from "components/src/components/Background/Background";
import { BackgroundProps } from "components/src/components/Background/types";

const chevronDown = { name: "ui_chevron_down", size: "sm" as SizeType };
const chevronUp = { name: "ui_chevron_up", size: "sm" as SizeType };

export const QuickPick = ({
  legs = [],
  onClick = () => {},
  price,
  title,
  disabled = false,
  selected = false,
  badge = "qp",
  promo,
  showBadge = false,
  showAvatar = true,
  showBorder = true,
  showTime = false,
  startTime,
  id,
  role = "group",
  bet = 20,
  mode = "light",
  height = "auto",
  maxHeight,
  minHeight,
  fluid,
  fullHeight = true,
  disableShowMoreClick,
  showExpandedFade = true,
  animation = true,
  eventDetails,
  isLive,
  setExpandedState,
  disableAutomatedTitle = false,
  iconColor,
  useAtForTeamMatchup,
  onExpandClick,
  onEditClick,
  className = "",
  bodyClasses = "",
  isEditable = false
}: QuickPickProps & { isEditable?: boolean }) => {
  const [isExpandable, setIsExpandable] = useState(
    height === "expandable" && !!maxHeight
  );
  const [hasExpanded, setHasExpanded] = useState(height === "expandable");

  const parsedIconString = parseIconString(get(promo, "image", null)) as IconStringReturnType;

  const promoName = get(promo, "name", null);
  const team = get(promo, "team", null);
  const teams = get(promo, "teams", []);

  const { foundTeam, foundTeams, foundLeague, hasFoundTeam, hasFoundTeams, hasFoundLeague } = useMemo(() => {
    const foundTeam =
      typeof parsedIconString === "object" && parsedIconString?.isTeam && parsedIconString?.foundTeam
        ? parsedIconString.foundTeam
        : getTeamInfo(team);
    const foundTeams = parsedIconString && Array.isArray(parsedIconString) ? parsedIconString.map((team)=> team.foundTeam) :  teams.map((team) => getTeamInfo(team));
    const hasFoundTeam = !!foundTeam;
    const hasFoundTeams = foundTeams.length !== 0;
    const foundLeague = typeof parsedIconString === "object" && parsedIconString?.isIcon ? getCompetitionInfo(parsedIconString?.league || parsedIconString?.name.replace(/_(onLight|onDark)$/, "")) : null;
    // console.log({foundLeague, parsedIconString, nameTransformed: parsedIconString?.name.replace(/_(onLight|onDark)$/, "")})
    const hasFoundLeague = !!foundLeague;
    return {
      foundTeam,
      foundTeams,
      foundLeague,
      hasFoundLeague,
      hasFoundTeam,
      hasFoundTeams,
    };
  }, [parsedIconString, team, teams]);

  const _forceReadability = useMemo(
    () => ({
      type:
        mode === "light" ? ("onLight" as "onLight") : ("onDark" as "onDark"),
    }),
    [mode]
  );

  const _isLive = isLive || eventDetails?.isLive;

  const computedTitle = useMemo(() => {
    if (title) return title;
    if (disableAutomatedTitle) return "";
    const _type = badge === "qp" ? "Parlay" : badge === "sgp" ? "SGP" : "";
    return `${legs.length} Leg ${_type}`;
  }, [title, disableAutomatedTitle]);

  const ariaLabel = `Quick Pick${
    computedTitle ? `, ${computedTitle},` : ""
  } with ${legs.length} legs ${promoName ? `, sponsored by ${promoName}` : ``}`;

  const isSingleLeg = legs?.length === 1;

  const computedLegs = useMemo(
    () =>
      computeLegs({
        badge,
        title: computedTitle,
        showBadge,
        legs,
        startTime,
        showTime,
        eventDetails,
        isLive: _isLive,
        isSingleLeg,
        disableAutomatedTitle
      }),
    [badge, title, showBadge, legs, showTime, startTime, eventDetails, _isLive, isSingleLeg, disableAutomatedTitle]
  );

  const backgroundProps = useMemo(() => {
    const sharedProps: BackgroundProps = {
      position: "static",
      wrapperClasses: "cui-z-2 cui-h-full cui-flex cui-justify-between cui-items-center cui-p-xs cui-gap-xs cui-pointer-events-none",
      width: "100%",
      colorMode: "both",
      fallbackPrimaryColor: "var(--fg-primary)",
      fallbackSecondaryColor: "var(--silver-1100)",
      colorType: "dark"
    };

    if (
      !hasFoundTeam &&
      !hasFoundTeams &&
      !_isLive &&
      typeof parsedIconString === "object" &&
      !parsedIconString?.isTeam &&
      !hasFoundLeague
    )
      return { ...sharedProps };

    if (_isLive) {
      return {
        primaryColor: "var(--highlight-accent)",
        secondaryColor: "var(--highlight-accent-subtle)",
        ...sharedProps,
      };
    }
    if (hasFoundTeams) {
      return {
        primaryColor: foundTeams?.at(0)?.metadata?.colors?.primary?.value,
        secondaryColor: foundTeams?.at(1)?.metadata?.colors?.primary?.value,
        ...sharedProps,
      };
    }
    if (hasFoundTeam)
      return {
        primaryColor: foundTeam?.metadata?.colors?.primary?.value,
        secondaryColor: foundTeam?.metadata?.colors?.secondary?.value,
        ...sharedProps,
      };
   if (hasFoundLeague) {
      return {
        primaryColor: foundLeague?.metadata?.colors?.primary?.value,
        secondaryColor: foundLeague?.metadata?.colors?.secondary?.value,
        ...sharedProps,
      };
    }
  }, [team, teams, _isLive, foundLeague]);

  const handleExpanded = useCallback(() => {
    if (disableShowMoreClick) {
      onExpandClick && onExpandClick({ id, isExpandable: !!setExpandedState });
    } else {
      setIsExpandable((v) => {
        onExpandClick && onExpandClick({ id, isExpandable: !v });
        return !v;
      });
    }
  }, [disableShowMoreClick, setExpandedState]);

  const handleExpand = useCallback(
    ({ canExpand = false, isExpandable }: Parameters<CardOnExpandType>[0]) => {
      setHasExpanded(canExpand);
    },
    []
  );

  useEffect(() => {
    if (
      height === "expandable" &&
      hasExpanded &&
      typeof setExpandedState !== "undefined"
    ) {
      setIsExpandable(setExpandedState);
    }
  }, [setExpandedState, hasExpanded, height]);
  console.log({fullHeight, height})
  return (
    <Card
      className={cx(
        {
          "cui-border-solid cui-border-1 cui-border-fg-minimal": showBorder,
          "cui-border-solid cui-border-1 cui-border-neutrals-transparent":
            !showBorder,
          "cui-w-full": fluid,
          "cui-w-[300px]": !fluid,
          "cui-h-full cui-grid-rows-[auto,1fr]": fullHeight && height === "auto" || height === "expandable",
          "cui-h-auto": !fullHeight || height !== "auto"
        },
        `cui-pb-xs cui-rounded-1 cui-box-border cui-bg-bg-default cui-relative ${className}` 
      )}
      cardBodyClasses={bodyClasses}
      role={role}
      aria-label={ariaLabel}
      id={id}
      tabIndex={0}
      borderColor="fg-minimal"
      rounded="md"
      noPad
      data-cy="quick-pick"
      expandable={height === "expandable"}
      maxHeight={height === "expandable" ? maxHeight : undefined}
      minHeight={minHeight ?? maxHeight}
      setCardState={isExpandable ? "expanded" : "open"}
      onExpand={handleExpand}
    >
      {/* ${!!computedLegs?.titleLeg ? "cui-min-h-1 cui-h-full" : "cui-h-1" } */}
      {backgroundProps ? (
        <Card.Header noPad className={`cui-flex cui-w-full cui-h-auto cui-self-start`}>
          <Background {...backgroundProps}>
            <div className="cui-flex cui-gap-xs cui-max-w-[calc(100%-44px)]">
              <div className="cui-inline-flex">{computedLegs?.titleLeg?.icon}</div>
              <span className="display-sm-bold cui-text-fg-global-white">{computedLegs?.titleLeg.text}</span>
            </div>
            <div className="cui-absolute cui-right-2 cui-top-2">
            <QuickPickAvatar
              {...{ showAvatar, image: parsedIconString, hasFoundLeague, foundLeague, promoName, mode, hasFoundTeam, hasFoundTeams, foundTeam, foundTeams, isLive: _isLive, iconColor, useAtForTeamMatchup }}
            />
            </div>
          </Background>
        </Card.Header>
      ) : null}

      <div
        className="cui-w-[96%] cui-flex cui-flex-grow cui-self-start cui-items-start cui-justify-between cui-px-xs cui-gap-xs cui-mt-xs"
        tabIndex={-1}
      >
        <QuickPickLegs computedLegs={computedLegs.legs} />
      </div>
      <Card.Footer
        className={cx(
          {
            "cui-absolute cui-pb-xs": hasExpanded && isExpandable,
            // "cui-pb-xs": !hasExpanded && height === "expandable",
          },
          "cui-flex cui-flex-col cui-px-xs cui-z-[1] cui-pt-xs cui-bg-bg-default cui-box-border"
        )}
        style={{
          // top: hasExpanded ? maxHeight - 68 : 0, // 68 + 6px from MarketButton height adjustment
          bottom: 0,
          width: hasExpanded ? "100%" : "",
        }}
      >
        {hasExpanded ? (
          <div
            className={cx(
              {
                "cui-absolute cui-w-full cui-h-10 cui-flex cui-items-end":
                  isExpandable,
                "cui-pb-xs": !isExpandable,
              },
              "cui-z-[1] cui-top-[-38px] cui-bottom-0"
            )}
            style={{
              background: showExpandedFade
                ? "linear-gradient(to top, var(--bg-default) 0 60%, var(--neutrals-transparent)) 80% 100%"
                : "",
            }}
          >
            <Button
              type="minimal"
              size="xs"
              onClick={handleExpanded}
              forceReadability={_forceReadability}
              className="cui-pt-2xs"
              rightIcon={isExpandable ? chevronDown : chevronUp}
            >
              {isExpandable
                ? `Show More (Total ${legs?.length} Legs)`
                : "Show Less"}
            </Button>
          </div>
        ) : null}
        {!hasExpanded ? <div className="cui-flex-grow" /> : null}
        <div className="cui-flex cui-gap-xs cui-items-center">
          <MarketButton
            id={`${id}-market-button`}
            odds={price}
            onClick={(e) => onClick(legs, e)}
            isSelected={selected}
            isDisabled={disabled}
            variant="promo"
            promoText={{ bet }}
            fluid={true}
            animation="off"
          />
          {isEditable && (
            <Button
              type="minimal"
              size="sm"
              leftIcon={{ name: "icon_ui_pencil", size: "sm" }}
              onClick={() => onEditClick?.(id)}
            >
              Edit
            </Button>
          )}
        </div>
      </Card.Footer>
    </Card>
  );
};
